import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, FlaskConical, RotateCcw, Eye } from 'lucide-react';
import Modal from '../components/ui/Modal.jsx';
import PdfPreviewModal from '../components/PdfPreviewModal.jsx';
import { api } from '../api/client.js';
import { formatDateFr } from '../lib/dates.js';

const STATUS_STYLES = {
  COMPLETED: { color: 'var(--success)', Icon: CheckCircle2, label: 'Terminé' },
  PARTIAL: { color: 'var(--warning)', Icon: AlertTriangle, label: 'Partiel' },
  FAILED: { color: 'var(--danger)', Icon: XCircle, label: 'Échec' },
  PENDING: { color: 'var(--text-muted)', Icon: Clock, label: 'En attente' },
  RUNNING: { color: 'var(--primary)', Icon: RefreshCw, label: 'En cours' },
};

const PAGE_SIZE = 20;

export default function HistoryView() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openJob, setOpenJob] = useState(null);
  const [retryResult, setRetryResult] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [pdfPreviewId, setPdfPreviewId] = useState(null);

  const loadJobs = (newOffset = offset) => {
    setLoading(true);
    api.cra
      .listJobs({ limit: PAGE_SIZE, offset: newOffset })
      .then(({ jobs, total }) => {
        setJobs(jobs);
        setTotal(total);
        setOffset(newOffset);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadJobs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = (jobId) => {
    api.cra.getJob(jobId).then(setOpenJob).catch(() => {});
  };

  const retryJob = async (jobId, e) => {
    e?.stopPropagation();
    setRetrying(true);
    setRetryResult(null);
    try {
      const result = await api.cra.retryJob(jobId);
      setRetryResult({ ok: true, ...result });
      loadJobs(offset);
    } catch (err) {
      setRetryResult({ ok: false, error: err.message });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <section style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h2 style={sectionTitleStyle}>
            Historique des générations ({total})
          </h2>
          <button
            onClick={() => loadJobs(offset)}
            disabled={loading}
            title="Rafraîchir"
            style={{
              background: 'transparent',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.4rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>

        {jobs.length === 0 && !loading && (
          <p style={{ color: 'var(--text-muted)' }}>Aucune génération enregistrée.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {jobs.map((j) => {
            const meta = STATUS_STYLES[j.status] ?? STATUS_STYLES.PENDING;
            const Icon = meta.Icon;
            return (
              <button
                key={j.id}
                onClick={() => openDetails(j.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--glass)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--text)',
                }}
              >
                <Icon size={18} color={meta.color} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {j.startDate?.slice(0, 10)} → {j.endDate?.slice(0, 10)}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {formatDateFr(j.startedAt)} · {j.totalPdfs} PDF · {j.totalConsultants}{' '}
                    consultants
                    {j.totalErrors > 0 && (
                      <span style={{ color: 'var(--danger)' }}> · {j.totalErrors} erreurs</span>
                    )}
                  </div>
                </div>
                {j.dryRun && (
                  <span
                    title="Mode dry-run"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.2rem 0.5rem',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: '0.7rem',
                      background: 'var(--warning)',
                      color: '#fff',
                    }}
                  >
                    <FlaskConical size={11} /> dry-run
                  </span>
                )}
                {(j.status === 'PARTIAL' || j.status === 'FAILED') && j.totalErrors > 0 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => retryJob(j.id, e)}
                    onKeyDown={(e) => e.key === 'Enter' && retryJob(j.id, e)}
                    title={`Relancer la génération sur les ${j.totalErrors} consultant(s) en erreur`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.6rem',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: '0.75rem',
                      background: 'var(--warning)',
                      color: '#fff',
                      cursor: retrying ? 'wait' : 'pointer',
                      opacity: retrying ? 0.6 : 1,
                    }}
                  >
                    <RotateCcw size={12} /> Relancer
                  </span>
                )}
                <span
                  style={{
                    padding: '0.25rem 0.6rem',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: '0.75rem',
                    color: meta.color,
                    border: `1px solid ${meta.color}`,
                  }}
                >
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>

        {retryResult && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-md)',
              background: retryResult.ok ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: retryResult.ok ? 'var(--success)' : 'var(--danger)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {retryResult.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            {retryResult.ok
              ? `Job ${retryResult.jobId.slice(0, 8)} relancé sur ${retryResult.retriedEmails.length} consultant(s)`
              : `Erreur : ${retryResult.error}`}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              onClick={() => loadJobs(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
              style={paginationButtonStyle(offset === 0 || loading)}
            >
              ← Précédent
            </button>
            <span style={{ alignSelf: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} sur {total}
            </span>
            <button
              onClick={() => loadJobs(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || loading}
              style={paginationButtonStyle(offset + PAGE_SIZE >= total || loading)}
            >
              Suivant →
            </button>
          </div>
        )}
      </section>

      <Modal
        open={openJob !== null}
        onClose={() => setOpenJob(null)}
        title={openJob ? `Job du ${formatDateFr(openJob.startedAt)}` : 'Détails'}
        size="xl"
      >
        {openJob && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
              <div>
                <strong>Statut :</strong> {openJob.status}
              </div>
              <div>
                <strong>Période :</strong> {openJob.startDate?.slice(0, 10)} →{' '}
                {openJob.endDate?.slice(0, 10)}
              </div>
              <div>
                <strong>Mode :</strong> {openJob.dryRun ? 'Dry-run' : 'Production'}
              </div>
              <div>
                <strong>Durée :</strong>{' '}
                {openJob.finishedAt
                  ? `${Math.round(
                      (new Date(openJob.finishedAt) - new Date(openJob.startedAt)) / 1000,
                    )}s`
                  : '—'}
              </div>
            </div>

            <details>
              <summary style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 600 }}>
                CRA générés ({openJob.craFiles?.length ?? 0})
              </summary>
              <div style={{ marginTop: '0.5rem', maxHeight: '240px', overflowY: 'auto' }}>
                {openJob.craFiles?.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      borderBottom: '1px solid var(--glass-border)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong>{c.consultantName}</strong> · {c.clientName} · {c.projectName} ·{' '}
                      {c.totalDays} j
                    </div>
                    <button
                      onClick={() => setPdfPreviewId(c.id)}
                      title="Prévisualiser le PDF"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.6rem',
                        background: 'transparent',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      <Eye size={12} /> Aperçu
                    </button>
                  </div>
                ))}
              </div>
            </details>

            <details open>
              <summary style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 600 }}>
                Logs ({Array.isArray(openJob.logs) ? openJob.logs.length : 0})
              </summary>
              <div
                style={{
                  marginTop: '0.5rem',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  background: 'var(--glass)',
                  padding: '0.75rem',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                  fontSize: '0.78rem',
                }}
              >
                {(Array.isArray(openJob.logs) ? openJob.logs : []).map((log, i) => (
                  <div key={i} style={{ padding: '0.2rem 0' }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {log.ts?.slice(11, 19)}
                    </span>{' '}
                    <strong>{log.type}</strong>
                    {log.message && ` — ${log.message}`}
                    {log.error && (
                      <span style={{ color: 'var(--danger)' }}> — {log.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </Modal>

      <PdfPreviewModal
        craFileId={pdfPreviewId}
        onClose={() => setPdfPreviewId(null)}
      />
    </div>
  );
}

const cardStyle = {
  background: 'var(--card-bg)',
  padding: '1.5rem',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleStyle = { margin: 0, fontSize: '1rem', color: 'var(--primary)' };
const paginationButtonStyle = (disabled) => ({
  padding: '0.4rem 0.9rem',
  background: 'var(--glass)',
  color: disabled ? 'var(--text-muted)' : 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '0.85rem',
});
